import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import SearchableCombobox from '../ui/SearchableCombobox';
import Button from '../ui/Button';
import Input from '../ui/Input';
import useApi from '../../hooks/useApi';
import { Send, Lock, ShieldAlert } from 'lucide-react';
import toast from 'react-hot-toast';

const ForwardModal = ({ isOpen, onClose, onConfirm, title = "Forward File", defaultWingId, defaultDepartmentId, defaultSectionId, hierarchyType = 'STANDARD', allowedSignedAs = ['Forwarder'] }) => {
    const { get } = useApi();
    
    // Hierarchy State
    const [wings, setWings] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [sections, setSections] = useState([]);
    const [users, setUsers] = useState([]);
    const [addressBookRecipients, setAddressBookRecipients] = useState([]);

    // Selection State
    const [wingId, setWingId] = useState('');
    const [departmentId, setDepartmentId] = useState('');
    const [sectionId, setSectionId] = useState('');
    const [recipientKey, setRecipientKey] = useState('');
    
    // PIN State
    const [pin, setPin] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const [signedAs, setSignedAs] = useState(allowedSignedAs[0] || 'Forwarder');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const mappedHierarchyUsers = users.map((u) => ({
        key: `user:${u.id}`,
        userId: u.id,
        name: u.name,
        designation: u.designation || u.role || 'Officer',
        address: u.officialAddress || u.address || '',
        email: u.email || '',
        sourceLabel: 'Hierarchy'
    }));

    const mappedAddressBook = (Array.isArray(addressBookRecipients) ? addressBookRecipients : []).map((entry) => {
        const isLinkedUser = String(entry?.sourceType || '') === 'user' || (entry?.linkedUserId !== null && entry?.linkedUserId !== undefined);
        const resolvedUserId = isLinkedUser ? (entry.linkedUserId ?? entry.sourceId ?? entry.id) : null;
        const displayName = entry.displayName || entry.contactPerson || entry.organizationName || entry.name || '';
        return {
            key: `${isLinkedUser ? 'user' : 'contact'}:${resolvedUserId ?? (entry.sourceId ?? entry.id)}`,
            userId: resolvedUserId,
            name: displayName,
            designation: entry.designation || entry.role || (isLinkedUser ? 'Officer' : 'Contact'),
            address: entry.address || entry.officialAddress || '',
            email: entry.email || '',
            sourceLabel: isLinkedUser ? 'Address Book Linked User' : 'Address Book Contact'
        };
    });

    const mergedRecipients = (() => {
        const byKey = new Map();
        [...mappedHierarchyUsers, ...mappedAddressBook].forEach((item) => {
            if (!item?.key) return;
            if (!byKey.has(item.key)) {
                byKey.set(item.key, item);
            }
        });
        return Array.from(byKey.values());
    })();

    const officerItems = mergedRecipients.map((u) => {
        const descriptionParts = [];
        if (u.userId !== undefined && u.userId !== null) descriptionParts.push(`User ID: ${u.userId}`);
        if (u.sourceLabel) descriptionParts.push(u.sourceLabel);
        if (u.address) descriptionParts.push(u.address);
        if (u.email) descriptionParts.push(u.email);
        return {
            value: String(u.key),
            label: `${u.name}${u.designation ? ` (${u.designation})` : ''}`,
            description: descriptionParts.join(' • '),
            searchText: [u.userId, u.name, u.designation, u.address, u.email, u.sourceLabel].filter(Boolean).join(' ')
        };
    });

    const selectedRecipient = mergedRecipients.find((r) => r.key === recipientKey);
    const selectedUserId = selectedRecipient?.userId ? String(selectedRecipient.userId) : '';

    const fetchAddressBookRecipients = async () => {
        try {
            const response = await get('/address-book?includeUsers=1');
            if (response.success) {
                setAddressBookRecipients(Array.isArray(response.data) ? response.data : []);
            }
        } catch (error) {
            console.error('Failed to fetch address book recipients', error);
        }
    };

    useEffect(() => {
        const initialize = async () => {
            if (isOpen) {
                // Reset states
                setWings([]);
                setDepartments([]);
                setSections([]);
                setUsers([]);
                setAddressBookRecipients([]);
                setWingId('');
                setDepartmentId('');
                setSectionId('');
                setRecipientKey('');
                setPin('');
                setMfaCode('');
                setSignedAs(allowedSignedAs[0] || 'Forwarder');

                try {
                    if (hierarchyType === 'STANDARD') {
                        // Load Wings
                        const wRes = await get('/hierarchy/wings');
                        if (wRes.success) setWings(wRes.data);
                        
                        // Apply Defaults for STANDARD
                        if (defaultWingId) {
                            setWingId(defaultWingId);
                            const dRes = await get(`/hierarchy/wings/${defaultWingId}/departments`);
                            if (dRes.success) {
                                setDepartments(dRes.data);
                                if (defaultDepartmentId) {
                                    setDepartmentId(defaultDepartmentId);
                                    const sRes = await get(`/hierarchy/departments/${defaultDepartmentId}/sections`);
                                    if (sRes.success) {
                                        setSections(sRes.data);
                                        if (defaultSectionId) {
                                            setSectionId(defaultSectionId);
                                            const uRes = await get(`/hierarchy/sections/${defaultSectionId}/users`);
                                            if (uRes.success) setUsers(uRes.data);
                                        }
                                    }
                                }
                            }
                        }
                    } else if (hierarchyType === 'SIMPLE') {
                        // Load Departments directly
                        const dRes = await get('/hierarchy/departments');
                        if (dRes.success) setDepartments(dRes.data);

                        if (defaultDepartmentId) {
                            setDepartmentId(defaultDepartmentId);
                            const sRes = await get(`/hierarchy/departments/${defaultDepartmentId}/sections`);
                            if (sRes.success) {
                                setSections(sRes.data);
                                if (defaultSectionId) {
                                    setSectionId(defaultSectionId);
                                    const uRes = await get(`/hierarchy/sections/${defaultSectionId}/users`);
                                    if (uRes.success) setUsers(uRes.data);
                                }
                            }
                        }
                    } else if (hierarchyType === 'FLAT') {
                        // Load Sections directly
                        const sRes = await get('/hierarchy/sections');
                        if (sRes.success) setSections(sRes.data);

                        if (defaultSectionId) {
                            setSectionId(defaultSectionId);
                            const uRes = await get(`/hierarchy/sections/${defaultSectionId}/users`);
                            if (uRes.success) setUsers(uRes.data);
                        }
                    }
                    await fetchAddressBookRecipients();
                } catch (e) { console.error(e); }
            }
        };
        initialize();
    }, [isOpen, defaultWingId, defaultDepartmentId, defaultSectionId, hierarchyType, allowedSignedAs]);

    const fetchWings = async () => {
        try {
            const response = await get('/hierarchy/wings');
            if (response.success) setWings(response.data);
        } catch (error) {
            console.error('Failed to fetch wings', error);
            toast.error('Failed to load wings. Please try again.');
        }
    };

    const fetchDepartments = async (wId) => {
        if (!wId) {
            setDepartments([]);
            return;
        }
        try {
            const response = await get(`/hierarchy/wings/${wId}/departments`);
            if (response.success) setDepartments(response.data);
        } catch (error) {
            console.error('Failed to fetch departments', error);
            toast.error('Failed to load departments. Please try again.');
        }
    };

    const fetchSections = async (dId) => {
        if (!dId) {
            setSections([]);
            return;
        }
        try {
            const response = await get(`/hierarchy/departments/${dId}/sections`);
            if (response.success) setSections(response.data);
        } catch (error) {
            console.error('Failed to fetch sections', error);
            toast.error('Failed to load sections. Please try again.');
        }
    };

    const fetchUsers = async (sId) => {
        if (!sId) {
            setUsers([]);
            return;
        }
        try {
            const response = await get(`/hierarchy/sections/${sId}/users`);
            if (response.success) setUsers(response.data);
        } catch (error) {
            console.error('Failed to fetch users', error);
            toast.error('Failed to load officers. Please try again.');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!recipientKey) {
            toast.error('Please select a recipient');
            return;
        }
        if (!selectedUserId) {
            toast.error('This contact has no linked user ID. Link it to a system user before forwarding.');
            return;
        }
        if (!pin || pin.length !== 4) {
            toast.error('Please enter a valid 4-digit PIN');
            return;
        }
        if (!mfaCode || String(mfaCode).trim().length < 6) {
            toast.error('Please enter your 6-digit MFA code');
            return;
        }
        if (!signedAs) {
            toast.error('Please select sign role');
            return;
        }

        if (isSubmitting) return;

        setIsSubmitting(true);
        try {
            await onConfirm(selectedUserId, pin, mfaCode, signedAs);
            onClose();
        } catch (error) {
            console.error(error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800 flex items-start gap-2">
                    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>
                        <span className="font-semibold">Secure Forwarding:</span> Select the destination carefully. This action will transfer custody of the file.
                    </div>
                </div>

                <div className="space-y-3">
                    {hierarchyType === 'STANDARD' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Wing</label>
                        <SearchableCombobox 
                            items={wings.map(w => ({ label: w.name, value: w.id }))}
                            value={wingId}
                            onChange={(val) => {
                                setWingId(val);
                                setDepartmentId('');
                                setSectionId('');
                                setRecipientKey('');
                                fetchDepartments(val);
                            }}
                            placeholder="Select Wing"
                        />
                    </div>
                    )}

                    {(hierarchyType === 'STANDARD' || hierarchyType === 'SIMPLE') && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Department</label>
                        <SearchableCombobox 
                            items={departments.map(d => ({ label: d.name, value: d.id }))}
                            value={departmentId}
                            onChange={(val) => {
                                setDepartmentId(val);
                                setSectionId('');
                                setRecipientKey('');
                                fetchSections(val);
                            }}
                            disabled={hierarchyType === 'STANDARD' && !wingId}
                            placeholder="Select Department"
                        />
                    </div>
                    )}

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Section</label>
                        <SearchableCombobox 
                            items={sections.map(s => ({ label: s.name, value: s.id }))}
                            value={sectionId}
                            onChange={(val) => {
                                setSectionId(val);
                                setRecipientKey('');
                                fetchUsers(val);
                            }}
                            disabled={(hierarchyType === 'STANDARD' || hierarchyType === 'SIMPLE') && !departmentId}
                            placeholder="Select Section"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Select Officer</label>
                        <SearchableCombobox 
                            items={officerItems}
                            value={recipientKey}
                            onChange={setRecipientKey}
                            placeholder="Select from hierarchy or address book"
                        />
                        <p className="text-xs text-gray-500 mt-1">Only recipients with a linked User ID can receive e-File custody.</p>
                    </div>

                    <div className="pt-2 border-t border-gray-100 mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Sign As</label>
                        <select
                            value={signedAs}
                            onChange={(e) => setSignedAs(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            disabled={allowedSignedAs.length <= 1}
                        >
                            {allowedSignedAs.map(r => (
                              <option key={r} value={r}>{r}</option>
                            ))}
                        </select>
                    </div>

                    <div className="pt-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">e-Sign PIN</label>
                        <Input 
                            type="password"
                            value={pin}
                            onChange={(e) => setPin(e.target.value)}
                            maxLength={4}
                            placeholder="Enter 4-digit PIN"
                            icon={Lock}
                            className="tracking-widest font-mono text-center"
                            autoComplete="off"
                        />
                        <p className="text-xs text-gray-500 mt-1 text-center">PIN + MFA is required for signing.</p>
                    </div>

                    <div className="pt-2">
                        <label className="block text-sm font-medium text-gray-700 mb-1">MFA Code</label>
                        <Input
                            type="text"
                            value={mfaCode}
                            onChange={(e) => setMfaCode(e.target.value)}
                            maxLength={6}
                            placeholder="Enter 6-digit code"
                            className="tracking-widest font-mono text-center"
                            autoComplete="one-time-code"
                            inputMode="numeric"
                        />
                    </div>
                </div>

                <div className="flex justify-end gap-3 mt-6 pt-2">
                    <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting || !recipientKey || !selectedUserId || !pin || !mfaCode} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                        <Send className="w-4 h-4 mr-2" />
                        Sign & Forward
                    </Button>
                </div>
            </form>
        </Modal>
    );
};

export default ForwardModal;
